import { useRecoilTransaction_UNSTABLE } from "recoil"

import type { TransactionOperation } from "."
import { checkWhetherAreasAreEditable } from "./checkWhetherAreasAreEditable"
import { checkWhetherAfterPhase } from "../../phase"
import { areaIndex, findAreaState } from "../areas"
import { edgeIndex } from "../edges"
import { nodeIndex } from "../nodes"
import { panelIndex } from "../panels"
import { areasOfPanelsIndexState } from "../relations"
import type { UnknownChangeForUnknownElement } from "../timeline"
import { divergeTimeline, generateElementChange } from "../timeline/transactions"

export const deleteArea: TransactionOperation<string> = (transactors, id) => {
  const { get, set } = transactors
  const areasAreEditable = checkWhetherAreasAreEditable(transactors)
  if (!areasAreEditable) {
    console.warn(`Areas are not editable`)

    return
  }
  const changes: UnknownChangeForUnknownElement[] = []
  const areasOfPanels = get(areasOfPanelsIndexState)
  const allEdgeIds = get(edgeIndex)
  const allAreaIds = get(areaIndex)
  const areaExists = allAreaIds.includes(id)
  if (areaExists) {
    const panelsToDelete = areasOfPanels.core.get(id)
    if (panelsToDelete) {
      set(panelIndex, (panelIds) =>
        panelIds.filter((panelId) => {
          if (!panelsToDelete.has(panelId)) return true
          changes.push(
            generateElementChange(transactors, {
              action: {
                type: `DELETE`,
                family: `PANEL`,
                id: panelId,
              },
            }),
          )
        }),
      )
    }
    const nodeIdsToKeep = new Set<string>()
    for (const areaId of allAreaIds) {
      if (areaId !== id) {
        const currentArea = get(findAreaState(areaId))
        for (const nodeId of currentArea.nodeIds) {
          nodeIdsToKeep.add(nodeId)
        }
      }
    }
    set(nodeIndex, (nodeIds) =>
      nodeIds.filter((nodeId) => {
        if (nodeIdsToKeep.has(nodeId)) return true
        changes.push(
          generateElementChange(transactors, {
            action: {
              type: `DELETE`,
              family: `NODE`,
              id: nodeId,
            },
          }),
        )
      }),
    )
    const edgeIdsToKeep = new Set<string>()
    for (const nodeId of nodeIdsToKeep) {
      for (const edgeId of allEdgeIds) {
        if (edgeId.includes(nodeId)) {
          for (const nodeId2 of nodeIdsToKeep) {
            if (edgeId.includes(nodeId2) && nodeId !== nodeId2) {
              for (const areaId of allAreaIds) {
                if (
                  areaId.includes(nodeId2) &&
                  areaId.includes(nodeId) &&
                  areaId !== id
                ) {
                  edgeIdsToKeep.add(edgeId)
                }
              }
            }
          }
        }
      }
    }
    set(edgeIndex, (edgeIds) =>
      edgeIds.filter((edgeId) => {
        if (edgeIdsToKeep.has(edgeId)) return true
        changes.push(
          generateElementChange(transactors, {
            action: {
              type: `DELETE`,
              family: `EDGE`,
              id: edgeId,
            },
          }),
        )
      }),
    )
    set(areaIndex, (ids) =>
      ids.filter((areaId) => {
        if (areaId !== id) return true
        changes.push(
          generateElementChange(transactors, {
            action: {
              type: `DELETE`,
              family: `AREA`,
              id,
            },
          }),
        )
      }),
    )
  }
  divergeTimeline(transactors, changes)
}

export const useDeleteArea = (id: string): VoidFunction =>
  useRecoilTransaction_UNSTABLE(
    (transactors) => () => deleteArea(transactors, id),
  )
